import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { api } from '@/lib/api';
import { AuthLayout } from './AuthLayout';
import { useAuthForm } from './useAuthForm';
import { Button } from '@/ui/Button';
import { Field, TextInput } from '@/ui/Field';
import { Notice } from '@/ui/Panel';

export function ForgotPassword({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const { busy, formError, fieldError, submit } = useAuthForm();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  return (
    <AuthLayout
      locale={locale}
      title={t('auth.forgotTitle')}
      lead={t('auth.forgotLead')}
      footer={
        <Link to={localePath(locale, '/login')} className="text-ink-muted underline decoration-line-strong underline-offset-2 hover:text-ink">
          {t('auth.signIn')}
        </Link>
      }
    >
      {sent ? (
        // Deliberately the same message whether or not the address has an
        // account: confirming existence would let anyone enumerate coaches.
        <Notice tone="ok">{t('auth.forgotSent')}</Notice>
      ) : (
        <form
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await submit(() => api.post('/auth/request-reset', { email }));
            if (ok) setSent(true);
          }}
        >
          {formError ? (
            <div className="mb-4">
              <Notice tone="risk">{formError}</Notice>
            </div>
          ) : null}

          <Field label={t('auth.email')} error={fieldError('email')}>
            {(p) => (
              <TextInput
                {...p}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                autoFocus
              />
            )}
          </Field>

          <Button type="submit" variant="primary" busy={busy} className="mt-2 w-full">
            {t('auth.sendLink')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
